/**
 * DeepLConfigDialog — DeepL 集成配置（全局单条加密 API Key）
 *
 * 镜像 AdaptyConfigDialog，但整个 admin 系统**一条全局 Key**（无 App 选择器）。接真
 * /api/v1/integrations/deepl/credential*（useDeeplCredential + deeplApi）。
 * 不变量：明文 Key **仅提交、永不回显**；只显 key_tail 掩码 + status。
 * 录入前后端先 ping /v2/usage 验证（invalid→拒、上游故障→503）；移除走 requireTyping 二次确认。
 * 全站翻译（MultiLangFormRows「从英文翻译」/ 书籍文案 / 反馈内容）的 Key 源 = 本凭证。
 */

import type { DeeplCredentialStatus } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useDeeplCredential } from '~/data/queries';
import { deeplApi } from '~/hooks/use-deepl';
import { Icons } from '~/lib/icons';
import { ConfirmDialog, Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { PasswordInput } from '~/ui/PasswordInput';
import { StatusChip } from '~/ui/StatusChip';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';

const STATUS_META: Record<DeeplCredentialStatus, { tone: 'pos' | 'warn' | 'neg'; label: string }> =
  {
    connected: { tone: 'pos', label: '已连接' },
    unverified: { tone: 'warn', label: '未验证' },
    invalid: { tone: 'neg', label: '失效' },
  };

function statusOf(s: string | null): DeeplCredentialStatus {
  return s === 'connected' || s === 'invalid' ? s : 'unverified';
}

/** Key 掩码：6 圆点 + 后 5 位（明文永不回显）*/
function deeplMask(tail: string): string {
  return `••••••${tail}`;
}

/** 录入 / 换 Key 表单：输 Key → 后端 ping 验证后加密存。 */
function DeeplKeyForm({ onCancel, onSaved }: { onCancel: () => void; onSaved: () => void }) {
  const [key, setKey] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const qc = useQueryClient();

  const save = useMutation({
    mutationFn: () => deeplApi.upsertKey({ api_key: key.trim() }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['integrations', 'deepl'] });
      toast('已保存并验证 DeepL Key', { tone: 'pos', icon: 'IconCheck' });
      onSaved();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : '保存失败'),
  });

  const submit = () => {
    setErr(null);
    if (key.trim().length < 8) return setErr('请输入有效的 DeepL API Key');
    save.mutate();
  };

  return (
    <div
      style={{
        padding: 14,
        background: 'var(--bg-subtle)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        marginTop: 12,
      }}
    >
      <FormRow
        label="DeepL API Key"
        required
        labelWidth={120}
        hint="DeepL Account → API keys 获取（free key 以 :fx 结尾）"
      >
        <PasswordInput
          className="input mono"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="DeepL Auth Key…"
          autoFocus
        />
        <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 6 }}>
          提交后加密存储，仅显示掩码、明文永不回显。
        </div>
        {err && (
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--neg)',
              marginTop: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <Icons.IconAlert size={12} />
            {err}
          </div>
        )}
      </FormRow>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="btn btn-accent" onClick={submit} disabled={save.isPending}>
          {save.isPending ? (
            <>
              <Icons.IconRefresh size={12} /> 验证中…
            </>
          ) : (
            <>
              <Icons.IconCheck size={12} /> 测试并保存
            </>
          )}
        </button>
        <button className="btn" onClick={onCancel} disabled={save.isPending}>
          取消
        </button>
      </div>
    </div>
  );
}

export function DeepLConfigDialog({ onClose }: { onClose: () => void }) {
  const { data: cred } = useDeeplCredential();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);

  const test = useMutation({
    mutationFn: () => deeplApi.testConnection(),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['integrations', 'deepl'] });
      const m = STATUS_META[statusOf(res.status)];
      toast(`连接测试：${m.label}`, {
        tone: res.status === 'connected' ? 'pos' : 'warn',
        icon: res.status === 'connected' ? 'IconCheck' : 'IconAlert',
      });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : '测试失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  const remove = useMutation({
    mutationFn: () => deeplApi.removeKey(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['integrations', 'deepl'] });
      toast('已移除 DeepL 凭证', { tone: 'warn', icon: 'IconTrash' });
      setRemoving(false);
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : '移除失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  const m = STATUS_META[statusOf(cred.status)];

  return (
    <Dialog
      title="DeepL 集成配置"
      subtitle="全局一条 API Key · 全站多语言翻译"
      icon="IconBranch"
      width={620}
      onClose={onClose}
      closeOnEsc={!removing}
      footer={
        <>
          <div style={{ flex: 1 }} />
          <button className="btn btn-accent" onClick={onClose}>
            <Icons.IconCheck size={12} /> 完成
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        {/* info 条 */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            padding: 12,
            background: 'var(--info-soft)',
            borderRadius: 7,
            fontSize: 12,
            color: 'var(--fg-muted)',
            lineHeight: 1.6,
            marginBottom: 16,
          }}
        >
          <Icons.IconInfo size={14} style={{ flexShrink: 0, marginTop: 2, color: 'var(--info)' }} />
          <span>
            整个后台共用一个 DeepL Key；Key 经 AES-GCM 加密存储，前端**永不回显明文**，仅显示后 5
            位掩码。全站「从英文翻译」/ 书籍文案 / 用户反馈翻译均用该 Key。未配置时翻译不可用。
          </span>
        </div>

        {!cred.configured && !editing ? (
          /* 空态 */
          <div className="card-flat" style={{ padding: '34px 18px', textAlign: 'center' }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'var(--bg-inset)',
                color: 'var(--fg-faint)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
              }}
            >
              <Icons.IconLock size={18} />
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 500, marginBottom: 4 }}>
              还没有配置 DeepL Key
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginBottom: 14 }}>
              录入 DeepL API Key 后，全站多语言「从英文翻译」即可使用。
            </div>
            <button className="btn btn-accent btn-sm" onClick={() => setEditing(true)}>
              <Icons.IconPlus size={12} /> 配置 DeepL Key
            </button>
          </div>
        ) : (
          /* 已配置：掩码 + 状态 + 操作 */
          !editing && (
            <Table className="tbl">
              <thead>
                <tr>
                  <th>API Key</th>
                  <th>最近验证</th>
                  <th style={{ width: 132 }} />
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span className="mono" style={{ fontSize: 11.5 }}>
                        {cred.key_tail ? deeplMask(cred.key_tail) : '—'}
                      </span>
                      <StatusChip tone={m.tone} dot label={m.label} size="sm" />
                    </div>
                  </td>
                  <td>
                    {cred.last_verified_at ? (
                      <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
                        {new Date(cred.last_verified_at).toLocaleString()}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>未验证</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-ghost btn-icon btn-sm"
                        title="测试连接"
                        onClick={() => test.mutate()}
                        disabled={test.isPending}
                      >
                        <Icons.IconRefresh size={13} />
                      </button>
                      <button
                        className="btn btn-ghost btn-icon btn-sm"
                        title="重新输入 Key"
                        onClick={() => setEditing(true)}
                      >
                        <Icons.IconEdit size={13} />
                      </button>
                      <button
                        className="btn btn-ghost btn-icon btn-sm"
                        title="移除"
                        style={{ color: 'var(--neg)' }}
                        onClick={() => setRemoving(true)}
                      >
                        <Icons.IconTrash size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </Table>
          )
        )}

        {/* 内联录入 / 换 Key 表单 */}
        {editing && (
          <DeeplKeyForm onCancel={() => setEditing(false)} onSaved={() => setEditing(false)} />
        )}
      </div>

      {removing && (
        <ConfirmDialog
          title="移除 DeepL 凭证"
          message="移除后全站「从英文翻译」/ 书籍文案 / 用户反馈翻译将不可用，需重新录入 Key。"
          tone="danger"
          confirmLabel="移除"
          requireTyping="DeepL"
          onConfirm={() => remove.mutate()}
          onClose={() => setRemoving(false)}
        />
      )}
    </Dialog>
  );
}
